import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

// Status badge component
function StatusBadge({ isActive }) {
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
      isActive 
        ? 'bg-green-100 text-green-700' 
        : 'bg-slate-100 text-slate-600 dark:text-slate-400'
    }`}>
      {isActive ? 'Active' : 'Draft'}
    </span>
  );
}

// Sequence card component
function SequenceCard({ sequence, onActivate, onDeactivate, onDelete }) {
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (sequence.is_active) {
        await onDeactivate(sequence.id);
      } else {
        await onActivate(sequence.id);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-900 dark:text-white">{sequence.name}</h3>
            <StatusBadge isActive={sequence.is_active} />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{sequence.description || 'No description'}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggle}
            disabled={loading || (sequence.steps?.length === 0 && !sequence.is_active)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              sequence.is_active 
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            } disabled:opacity-50`}
            title={sequence.steps?.length === 0 ? 'Add steps first' : ''}
          >
            {loading ? '...' : sequence.is_active ? 'Pause' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-slate-900 dark:text-white">{sequence.steps?.length || 0}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Steps</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-slate-900 dark:text-white">{sequence.enrolled_count || 0}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Enrolled</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-slate-900 dark:text-white">{sequence.completed_count || 0}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Completed</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {sequence.trigger === 'manual' ? 'Manual' : sequence.trigger === 'new_lead' ? 'Auto' : 'Status'}
          </p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Trigger</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700">
        <span className="text-xs text-slate-400">
          Created {sequence.created_at ? format(parseISO(sequence.created_at), 'MMM d, yyyy') : 'N/A'}
        </span>
        
        <div className="flex items-center gap-2">
          <Link
            to={`/sequences/${sequence.id}`}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            View Details →
          </Link>
          <Link
            to={`/sequences/${sequence.id}/edit`}
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300"
          >
            Edit
          </Link>
          <button
            onClick={() => onDelete(sequence.id)}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Sequences Page
export default function SequencesPage() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, draft
  const navigate = useNavigate();

  const fetchSequences = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { is_active: filter === 'active' } : {};
      const response = await axios.get(`${API}/sequences`, { params });
      setSequences(response.data);
    } catch (error) {
      console.error('Error fetching sequences:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchSequences();
  }, [fetchSequences]);

  const handleActivate = async (id) => {
    try {
      await axios.post(`${API}/sequences/${id}/activate`);
      fetchSequences();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to activate sequence');
    }
  };

  const handleDeactivate = async (id) => {
    await axios.post(`${API}/sequences/${id}/deactivate`);
    fetchSequences();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sequence?')) return;
    await axios.delete(`${API}/sequences/${id}`);
    fetchSequences();
  };

  // Stats
  const totalSequences = sequences.length;
  const activeSequences = sequences.filter(s => s.is_active).length;
  const totalEnrolled = sequences.reduce((sum, s) => sum + (s.enrolled_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="sequences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Email Sequences</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Automate your outreach with multi-step email campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/reply-inbox"
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Reply Inbox
          </Link>
          <Link
            to="/sequences/new"
            className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Sequence
          </Link>
        </div>
      </div>

      {/* Stats Cards - Clean style with BLACK values */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Sequences</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{totalSequences}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Active</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{activeSequences}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Leads Enrolled</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{totalEnrolled}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-1 p-2 border-b border-slate-100 dark:border-slate-700">
          {['all', 'active', 'draft'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === f 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Drafts'}
            </button>
          ))}
        </div>

        {/* Sequences Grid */}
        <div className="p-4">
          {sequences.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No sequences yet</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Create your first email sequence to automate outreach</p>
              <Link
                to="/sequences/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Sequence
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {sequences.map(sequence => (
                <SequenceCard
                  key={sequence.id}
                  sequence={sequence}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
